// IpInvalidDetail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";

const API_BASE =
  process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000/dmarc";

// Reutilizamos utilidades del otro componente
function toBool(v) {
  if (v === true || v === false) return v;
  if (v == null) return false;
  const s = String(v).trim().toLowerCase();
  return ["true", "t", "1", "yes", "y", "ok", "si", "sí"].includes(s);
}
function BoolPill({ value }) {
  const on = toBool(value);
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${on ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
    >
      {on ? "Sí" : "No"}
    </span>
  );
}

export default function IpInvalidDetail() {
  const { ip } = useParams();
  const { search } = useLocation();
  const qs = new URLSearchParams(search);

  const dominio = qs.get("dominio") || "";
  const desde = qs.get("desde") || "";
  const hasta = qs.get("hasta") || "";
  const headerFrom = qs.get("header_from") || "";
  const fuente = qs.get("fuente") || "";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancel = false;
    async function run() {
      try {
        setLoading(true);
        setErr("");

        if (!ip || !headerFrom || !fuente) {
          throw new Error("Faltan parámetros: ip, header_from o fuente");
        }

        const url =
          `${API_BASE}/detalle_ip_no_dmarc` +
          `?ip=${encodeURIComponent(ip)}` +
          `&header_from=${encodeURIComponent(headerFrom)}` +
          `&fuente=${encodeURIComponent(fuente)}` +
          `&dominio=${encodeURIComponent(dominio)}` +
          `&desde=${encodeURIComponent(desde)}` +
          `&hasta=${encodeURIComponent(hasta)}`;

        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancel) setData(Array.isArray(json) ? json : []);
      } catch (e) {
        if (!cancel) setErr(String(e.message || e));
      } finally {
        if (!cancel) setLoading(false);
      }
    }
    run();
    return () => {
      cancel = true;
    };
  }, [ip, headerFrom, fuente, dominio, desde, hasta]);

  // Cabecera: tomamos la primera fila para metadatos; el total lo sumamos
  const header = useMemo(() => data[0] || {}, [data]);
  const totalCount = useMemo(
    () => data.reduce((acc, r) => acc + (Number(r.count) || 0), 0),
    [data],
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Detalle IP inválida — {ip}</h1>
        <Link to="/" className="text-blue-600 underline">
          ← Volver
        </Link>
      </div>

      {loading && <div>Cargando…</div>}
      {err && <div className="text-red-600">Error: {err}</div>}

      {!loading && !err && (
        <>
          {/* Header con datos del bucket */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl shadow p-4">
              <div className="text-sm text-gray-500">IP Fuente</div>
              <div className="font-mono">{header.sourceip || ip}</div>
            </div>
            <div className="bg-white rounded-2xl shadow p-4">
              <div className="text-sm text-gray-500">Dominio (header_from)</div>
              <div>{header.domain || header.dominio || headerFrom || "-"}</div>
            </div>
            <div className="bg-white rounded-2xl shadow p-4">
              <div className="text-sm text-gray-500">Origen (report org)</div>
              <div>{header.reportorgname || fuente || "-"}</div>
            </div>
            <div className="bg-white rounded-2xl shadow p-4">
              <div className="text-sm text-gray-500">
                Total correos (fallidos en bucket)
              </div>
              <div>{totalCount}</div>
            </div>
          </div>

          {/* Tabla: solo records que fallan (el backend ya filtra) */}
          <section className="bg-white rounded-2xl shadow p-5">
            <h2 className="text-lg font-medium mb-3">
              Autenticaciones (solo fallidas en este bucket)
            </h2>
            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-100 text-left">
                    <th className="px-3 py-2">RecordID</th>
                    <th className="px-3 py-2">AuthType</th>
                    <th className="px-3 py-2">Resultado</th>
                    <th className="px-3 py-2">Selector</th>
                    <th className="px-3 py-2">DKIM Domain</th>
                    <th className="px-3 py-2">SPF Domain</th>
                    <th className="px-3 py-2">DKIM Align</th>
                    <th className="px-3 py-2">SPF Align</th>
                    <th className="px-3 py-2">Disposición</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.length ? (
                    data.map((row, i) => (
                      <tr
                        key={`${row.recordid}-${row.authtype}-${i}`}
                        className="border-b last:border-0"
                      >
                        <td className="px-3 py-2">
                          <Link
                            to={`/record/${row.recordid}`}
                            className="underline text-blue-600 hover:text-blue-800"
                            title="Ver detalle del record"
                          >
                            {row.recordid}
                          </Link>
                        </td>
                        <td className="px-3 py-2">{row.authtype}</td>
                        <td className="px-3 py-2">{row.result}</td>
                        <td className="px-3 py-2">{row.selector ?? "-"}</td>
                        <td className="px-3 py-2">
                          {row.ar_dkim_domain || row.dkimdomain || "-"}
                        </td>
                        <td className="px-3 py-2">
                          {row.ar_spf_domain || row.spfdomain || "-"}
                        </td>
                        <td className="px-3 py-2">
                          <BoolPill value={row.dkimaligned} />
                        </td>
                        <td className="px-3 py-2">
                          <BoolPill value={row.spfaligned} />
                        </td>
                        <td className="px-3 py-2">{row.disposition || "-"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="px-3 py-3 text-gray-500" colSpan={9}>
                        Sin autenticaciones fallidas
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
